<div class="kb-page">
  <!-- ======================================================= top bar -->
  <header class="kb-topbar">
    <div class="kb-brand">
      <img src="assets/login/header-logo.png" alt="Home" class="kb-logo" (click)="goHome()" title="Back to home" />
      <nav class="kb-sections" aria-label="Vault sections">
        @if (hasMediaSection) {
          <button type="button" class="kb-section" (click)="goMedia()">
            <i class="fas fa-photo-video"></i> Media
          </button>
        }
        <button type="button" class="kb-section active" (click)="goKnowledgeHome()">
          <i class="fas fa-book"></i> Knowledge base
        </button>
      </nav>
      <a class="kb-beta" routerLink="/whats-new" title="Still being built. Use it, and tell Manish what is off.">Beta</a>
    </div>

    <div class="kb-search" role="search">
      <i class="fas fa-search"></i>
      <input
        type="text"
        placeholder="Search everything you can see: SOPs, policies, templates, links…"
        [ngModel]="searchQuery"
        (ngModelChange)="onSearchInput($event)"
        aria-label="Search the knowledge base"
      />
      @if (searchQuery) {
        <button type="button" class="kb-clear" (click)="clearSearch()" title="Clear search">
          <i class="fas fa-times"></i>
        </button>
      }
    </div>

    <div class="kb-topbar-right">
      @if (me?.canCreateFolders && view !== 'search') {
        <button
          type="button"
          class="kb-btn kb-btn-primary"
          (click)="openCreateFolder(view === 'folder' ? folder : null)"
          [disabled]="view === 'folder' && (!folder?.canWrite || (folder?.depth || 1) >= 2)"
          [title]="view === 'folder' && (folder?.depth || 1) >= 2 ? 'Folders go one level deep' : ''"
        >
          <i class="fas fa-folder-plus"></i>
          {{ view === 'folder' ? 'New sub-folder' : 'New folder' }}
        </button>
      }
    </div>
  </header>

  <div class="kb-body">
    <!-- ===================================================== left rail -->
    <aside class="kb-rail">
      <div class="kb-rail-title">Spaces</div>
      @for (s of spaces; track s.key) {
        <button
          type="button"
          class="kb-space"
          [class.active]="space === s.key && view !== 'search'"
          (click)="goKnowledgeHome(s.key)"
        >
          <i class="fas {{ s.icon }}"></i>
          <span class="kb-space-text">
            <span class="kb-space-label">{{ s.label }}</span>
            <span class="kb-space-hint">{{ s.hint }}</span>
          </span>
        </button>
      }
      <div class="kb-rail-note">
        @if (me?.canCreateFolders) {
          You can create folders and add to any shared folder you can see.
        } @else {
          You can read everything shared with you. A department head, HR or Admin adds material.
        }
      </div>
    </aside>

    <!-- ===================================================== main -->
    <main class="kb-main">
      <!-- ---------- search results ---------- -->
      @if (view === 'search') {
        <section class="kb-results">
          <div class="kb-section-head">
            <h2>Results for “{{ searchQuery }}”</h2>
            @if (searching) {
              <span class="kb-muted">Searching…</span>
            } @else if (searchResults) {
              <span class="kb-muted">
                {{ searchResults.items.length }} item{{ searchResults.items.length === 1 ? '' : 's' }}
                @if (searchResults.folders.length) {
                  · {{ searchResults.folders.length }} folder{{ searchResults.folders.length === 1 ? '' : 's' }}
                }
              </span>
            }
          </div>
          @if (searchResults?.folders?.length) {
            <div class="kb-chip-row kb-folder-chips">
              @for (f of searchResults!.folders; track f._id) {
                <button type="button" class="kb-folder-chip" (click)="openFolder(f)">
                  <i class="fas fa-folder"></i> {{ f.path || f.name }}
                </button>
              }
            </div>
          }
          @if (searchResults && !searching && !searchResults.items.length && !searchResults.folders.length) {
            <div class="kb-empty">
              <i class="fas fa-search"></i>
              <p>Nothing matches “{{ searchQuery }}” in the folders you can see.</p>
              <small>Search looks at titles, descriptions, tags, file names and what a saved link’s page says.</small>
            </div>
          }
          <div class="kb-list">
            @for (item of searchResults?.items || []; track item._id) {
              <ng-container *ngTemplateOutlet="itemCard; context: { $implicit: item, showPath: true }"></ng-container>
            }
          </div>
        </section>
      }

      <!-- ---------- a folder ---------- -->
      @else if (view === 'folder') {
        <section class="kb-folder-view">
          <nav class="kb-crumbs" aria-label="Breadcrumb">
            <button type="button" (click)="goKnowledgeHome()">{{ spaceTitle }}</button>
            @for (b of breadcrumb; track b._id; let last = $last) {
              <i class="fas fa-chevron-right"></i>
              @if (last) {
                <span class="current">{{ b.name }}</span>
              } @else {
                <button type="button" (click)="openFolder(b)">{{ b.name }}</button>
              }
            }
          </nav>

          <div class="kb-folder-head">
            <div class="kb-folder-head-text">
              <h2>
                {{ folder?.name }}
                @if (folder) {
                  <span class="kb-vis {{ folder.visibility }}">{{ visibilityLabel(folder) }}</span>
                }
              </h2>
              @if (folder?.description) {
                <p class="kb-muted">{{ folder?.description }}</p>
              }
              <p class="kb-muted small">
                {{ folder?.itemCount || 0 }} item{{ folder?.itemCount === 1 ? '' : 's' }}
                · {{ folder?.subFolderCount || 0 }} sub-folder{{ folder?.subFolderCount === 1 ? '' : 's' }}
                · created by {{ folder?.creatorName }}
              </p>
            </div>
            <div class="kb-actions">
              @if (folder?.canWrite) {
                <button type="button" class="kb-btn" (click)="openUpload()"><i class="fas fa-upload"></i> Add files</button>
                <button type="button" class="kb-btn" (click)="openLink()"><i class="fas fa-link"></i> Save a link</button>
              }
              @if (folder?.canManage) {
                <button type="button" class="kb-icon-btn" (click)="openEditFolder(folder!)" title="Rename or change who sees it">
                  <i class="fas fa-pen"></i>
                </button>
                <button type="button" class="kb-icon-btn danger" (click)="deleteFolder(folder!)" title="Delete this folder">
                  <i class="fas fa-trash"></i>
                </button>
              }
            </div>
          </div>

          @if (subFolders.length) {
            <div class="kb-subfolders">
              @for (f of subFolders; track f._id) {
                <button type="button" class="kb-subfolder" (click)="openFolder(f)">
                  <i class="fas fa-folder"></i> {{ f.name }}
                  <span class="kb-count">{{ f.itemCount }}</span>
                </button>
              }
            </div>
          }

          <div class="kb-toolbar">
            <div class="kb-chip-row">
              @for (t of typeFilters; track t.value) {
                <button type="button" class="kb-chip" [class.active]="typeFilter === t.value" (click)="setType(t.value)">
                  <i class="fas {{ t.icon }}"></i> {{ t.label }}
                </button>
              }
            </div>
            <div class="kb-toolbar-right">
              <div class="kb-inline-search">
                <i class="fas fa-search"></i>
                <input type="text" placeholder="Filter in this folder" [ngModel]="folderQuery" (ngModelChange)="onFolderQuery($event)" />
              </div>
              <select class="kb-select" [ngModel]="sort" (ngModelChange)="setSort($event)" aria-label="Sort">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="title">A → Z</option>
              </select>
            </div>
          </div>

          @if (tagFilter) {
            <div class="kb-active-filter">
              Showing tag <strong>#{{ tagFilter }}</strong>
              <button type="button" (click)="setTag(tagFilter)" title="Remove tag filter"><i class="fas fa-times"></i></button>
            </div>
          }

          @if (loading) {
            <div class="kb-loading"><span class="kb-spinner"></span> Loading…</div>
          } @else if (!items.length) {
            <div class="kb-empty">
              <i class="fas fa-folder-open"></i>
              @if (typeFilter || tagFilter || folderQuery) {
                <p>Nothing in this folder matches the filter.</p>
              } @else {
                <p>Nothing here yet.</p>
                @if (folder?.canWrite) {
                  <small>Add files, or save a link to a Google Doc, Sheet or Notion page — links take no storage.</small>
                }
              }
            </div>
          }

          <div class="kb-list">
            @for (item of items; track item._id) {
              <ng-container *ngTemplateOutlet="itemCard; context: { $implicit: item, showPath: false }"></ng-container>
            }
          </div>

          @if (pageCount > 1) {
            <div class="kb-pager">
              <button type="button" class="kb-btn" [disabled]="page <= 1" (click)="loadItems(page - 1)">
                <i class="fas fa-chevron-left"></i> Previous
              </button>
              <span class="kb-muted">Page {{ page }} of {{ pageCount }}</span>
              <button type="button" class="kb-btn" [disabled]="page >= pageCount" (click)="loadItems(page + 1)">
                Next <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          }
        </section>
      }

      <!-- ---------- home: a space ---------- -->
      @else {
        <section class="kb-home">
          <div class="kb-section-head">
            <h2>{{ spaceTitle }}</h2>
            <span class="kb-muted">{{ topFolders.length }} folder{{ topFolders.length === 1 ? '' : 's' }}</span>
          </div>

          @if (loading) {
            <div class="kb-loading"><span class="kb-spinner"></span> Loading…</div>
          } @else if (!topFolders.length) {
            <div class="kb-empty">
              <i class="fas fa-folder-plus"></i>
              @switch (space) {
                @case ('company') {
                  <p>No company folders yet.</p>
                }
                @case ('department') {
                  <p>Your department has no folders yet.</p>
                }
                @case ('private') {
                  <p>No private folders yet. Only you can see what you keep here.</p>
                }
                @default {
                  <p>No department folders yet.</p>
                }
              }
              @if (me?.canCreateFolders) {
                <button type="button" class="kb-btn kb-btn-primary" (click)="openCreateFolder(null)">
                  <i class="fas fa-folder-plus"></i> Create the first folder
                </button>
              }
            </div>
          } @else if (space === 'all-departments') {
            @for (g of departmentGroups; track g.department) {
              <h3 class="kb-group">{{ g.department }}</h3>
              <div class="kb-folder-grid">
                @for (f of g.folders; track f._id) {
                  <ng-container *ngTemplateOutlet="folderCard; context: { $implicit: f }"></ng-container>
                }
              </div>
            }
          } @else {
            <div class="kb-folder-grid">
              @for (f of topFolders; track f._id) {
                <ng-container *ngTemplateOutlet="folderCard; context: { $implicit: f }"></ng-container>
              }
            </div>
          }

          @if (recent.length) {
            <h3 class="kb-group">Recently added</h3>
            <div class="kb-list">
              @for (item of recent; track item._id) {
                <ng-container *ngTemplateOutlet="itemCard; context: { $implicit: item, showPath: true }"></ng-container>
              }
            </div>
          }
        </section>
      }
    </main>
  </div>

  <!-- ======================================================= templates -->
  <ng-template #folderCard let-f>
    <div class="kb-folder" (click)="openFolder(f)" role="button" tabindex="0" (keyup.enter)="openFolder(f)">
      <div class="kb-folder-top">
        <i class="fas fa-folder kb-folder-glyph"></i>
        <span class="kb-vis {{ f.visibility }}">{{ visibilityLabel(f) }}</span>
        @if (f.canManage) {
          <div class="kb-folder-menu" (click)="$event.stopPropagation()">
            <button type="button" class="kb-icon-btn" title="Rename or change who sees it" (click)="openEditFolder(f)">
              <i class="fas fa-pen"></i>
            </button>
            <button type="button" class="kb-icon-btn danger" title="Delete" (click)="deleteFolder(f)">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        }
      </div>
      <div class="kb-folder-name">{{ f.name }}</div>
      @if (f.description) {
        <div class="kb-folder-desc">{{ f.description }}</div>
      }
      <div class="kb-folder-meta">
        {{ f.itemCount }} item{{ f.itemCount === 1 ? '' : 's' }}
        @if (f.subFolderCount) {
          · {{ f.subFolderCount }} sub-folder{{ f.subFolderCount === 1 ? '' : 's' }}
        }
        · {{ f.creatorName }}
      </div>
    </div>
  </ng-template>

  <ng-template #itemCard let-item let-showPath="showPath">
    <div class="kb-item" (click)="openItem(item)" role="button" tabindex="0" (keyup.enter)="openItem(item)">
      <div class="kb-item-icon {{ typeClass(item) }}"><i class="{{ iconFor(item) }}"></i></div>
      <div class="kb-item-body">
        <div class="kb-item-title">
          {{ item.title }}
          @if ((item.versions?.length || 0) > 1) {
            <span class="kb-vcount" title="{{ item.versions.length }} versions">v{{ item.versions.length }}</span>
          }
        </div>
        @if (item.description) {
          <div class="kb-item-desc">{{ item.description }}</div>
        }
        <div class="kb-item-meta">
          <span class="kb-type">{{ typeLabel(item) }}</span>
          @if (item.kind === 'file') {
            <span>{{ size(item.size) }}</span>
          }
          <span>{{ item.addedByName }} · {{ when(item.createdAt) }}</span>
          @if (showPath && item.path) {
            <span class="kb-path"><i class="fas fa-folder"></i> {{ item.path }}</span>
          }
        </div>
        @if (item.tags?.length) {
          <div class="kb-tags">
            @for (t of item.tags; track t) {
              <button type="button" class="kb-tag" [class.active]="tagFilter === t" (click)="$event.stopPropagation(); onTagClick(t)">
                #{{ t }}
              </button>
            }
          </div>
        }
      </div>
      <div class="kb-item-actions" (click)="$event.stopPropagation()">
        <button type="button" class="kb-icon-btn" title="{{ item.kind === 'link' ? 'Open the link' : 'Open the file' }}" (click)="open(item)">
          <i class="fas fa-external-link-alt"></i>
        </button>
      </div>
    </div>
  </ng-template>

  <!-- ======================================================= item drawer -->
  @if (drawer.open && drawer.item) {
    <div class="kb-overlay" (click)="closeDrawer()"></div>
    <aside class="kb-drawer" role="dialog" aria-modal="true">
      <header class="kb-drawer-head">
        <div class="kb-item-icon large {{ typeClass(drawer.item) }}"><i class="{{ iconFor(drawer.item) }}"></i></div>
        <div class="kb-drawer-title">
          @if (!drawer.editing) {
            <h3>{{ drawer.item.title }}</h3>
          } @else {
            <h3>Edit</h3>
          }
          <div class="kb-crumbs small">
            @for (b of drawer.breadcrumb; track b._id; let last = $last) {
              <button type="button" (click)="closeDrawer(); openFolder(b)">{{ b.name }}</button>
              @if (!last) {
                <i class="fas fa-chevron-right"></i>
              }
            }
          </div>
        </div>
        <button type="button" class="kb-icon-btn" (click)="closeDrawer()" title="Close"><i class="fas fa-times"></i></button>
      </header>

      <div class="kb-drawer-body">
        @if (drawer.editing) {
          <label class="kb-field">
            <span>Title</span>
            <input type="text" [(ngModel)]="drawer.draft.title" maxlength="160" />
          </label>
          <label class="kb-field">
            <span>Description</span>
            <textarea rows="4" [(ngModel)]="drawer.draft.description" maxlength="2000" placeholder="What is this, who is it for, when does it apply?"></textarea>
          </label>
          <label class="kb-field">
            <span>Tags <small>(comma-separated)</small></span>
            <input type="text" [(ngModel)]="drawer.draft.tags" placeholder="onboarding, hr, template" />
          </label>
          @if (drawer.item.kind === 'link') {
            <label class="kb-field">
              <span>Address</span>
              <input type="url" [(ngModel)]="drawer.draft.url" />
            </label>
          }
          <div class="kb-row-end">
            <button type="button" class="kb-btn" (click)="cancelEdit()" [disabled]="drawer.saving">Cancel</button>
            <button type="button" class="kb-btn kb-btn-primary" (click)="saveEdit()" [disabled]="drawer.saving">
              {{ drawer.saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        } @else {
          @if (drawer.item.description) {
            <p class="kb-drawer-desc">{{ drawer.item.description }}</p>
          }
          @if (drawer.item.tags?.length) {
            <div class="kb-tags">
              @for (t of drawer.item.tags; track t) {
                <button type="button" class="kb-tag" (click)="closeDrawer(); onTagClick(t)">#{{ t }}</button>
              }
            </div>
          }
          <dl class="kb-meta-grid">
            <dt>Type</dt>
            <dd>{{ typeLabel(drawer.item) }}</dd>
            @if (drawer.item.kind === 'file') {
              <dt>Size</dt>
              <dd>{{ size(drawer.item.size) }}</dd>
              <dt>File</dt>
              <dd class="kb-break">{{ drawer.item.fileName }}</dd>
            } @else {
              <dt>Address</dt>
              <dd class="kb-break"><a [href]="drawer.item.link?.url" target="_blank" rel="noopener">{{ drawer.item.link?.url }}</a></dd>
            }
            <dt>Added</dt>
            <dd>{{ drawer.item.addedByName }} · {{ when(drawer.item.createdAt) }}</dd>
            @if (drawer.item.updatedByName) {
              <dt>Updated</dt>
              <dd>{{ drawer.item.updatedByName }} · {{ when(drawer.item.updatedAt) }}</dd>
            }
          </dl>
          @if (drawer.item.link?.image) {
            <img class="kb-link-preview" [src]="drawer.item.link!.image" alt="" />
          }
          <button type="button" class="kb-btn kb-btn-primary kb-open-btn" (click)="open(drawer.item)">
            <i class="fas fa-external-link-alt"></i>
            {{ drawer.item.kind === 'link' ? 'Open the link' : 'Open the file' }}
          </button>

          @if (drawer.item.kind === 'file') {
            <h4 class="kb-sub-head">
              Versions <span class="kb-count">{{ drawer.item.versions?.length || 1 }}</span>
            </h4>
            <ul class="kb-versions">
              @for (v of (drawer.item.versions || []).slice().reverse(); track v._id; let i = $index) {
                <li>
                  <div class="kb-version-main">
                    <a [href]="v.fileUrl" target="_blank" rel="noopener" class="kb-break">{{ v.fileName }}</a>
                    @if (i === 0) {
                      <span class="kb-badge">current</span>
                    }
                  </div>
                  <div class="kb-muted small">
                    {{ size(v.size) }} · {{ v.uploadedByName }} · {{ when(v.createdAt) }}
                    @if (v.note) {
                      — {{ v.note }}
                    }
                  </div>
                </li>
              }
            </ul>
            @if (drawer.item.canEdit) {
              <div class="kb-new-version">
                <input type="text" [(ngModel)]="drawer.versionNote" placeholder="What changed in this version? (optional)" [disabled]="drawer.versionBusy" />
                <button type="button" class="kb-btn" (click)="pickVersion()" [disabled]="drawer.versionBusy">
                  <i class="fas fa-upload"></i>
                  {{ drawer.versionBusy ? 'Uploading ' + drawer.versionPercent + '%' : 'Upload a new version' }}
                </button>
              </div>
            }
          }
        }
      </div>

      @if (!drawer.editing) {
        <footer class="kb-drawer-foot">
          @if (drawer.item.canEdit) {
            <button type="button" class="kb-btn" (click)="startEdit()"><i class="fas fa-pen"></i> Edit</button>
            <button type="button" class="kb-btn" (click)="openMove(drawer.item)"><i class="fas fa-folder-open"></i> Move</button>
          }
          @if (drawer.item.canDelete) {
            <button type="button" class="kb-btn danger" (click)="deleteItem(drawer.item)"><i class="fas fa-trash"></i> Delete</button>
          }
        </footer>
      }
    </aside>
  }

  <!-- ======================================================= folder modal -->
  @if (folderModal.open) {
    <div class="kb-overlay" (click)="closeFolderModal()"></div>
    <div class="kb-modal" role="dialog" aria-modal="true">
      <header>
        <h3>
          @if (folderModal.mode === 'edit') {
            Edit folder
          } @else if (folderModal.parent) {
            New sub-folder in “{{ folderModal.parent.name }}”
          } @else {
            New folder
          }
        </h3>
        <button type="button" class="kb-icon-btn" (click)="closeFolderModal()"><i class="fas fa-times"></i></button>
      </header>
      <div class="kb-modal-body">
        <label class="kb-field">
          <span>Name</span>
          <input type="text" [(ngModel)]="folderModal.name" maxlength="160" placeholder="e.g. Onboarding, Brand guidelines, Vendor SOPs" autofocus />
        </label>
        <label class="kb-field">
          <span>Description <small>(optional)</small></span>
          <textarea rows="3" [(ngModel)]="folderModal.description" maxlength="2000" placeholder="One line on what belongs here"></textarea>
        </label>
        @if (!folderModal.parent && !(folderModal.mode === 'edit' && folderModal.target?.parentFolderId)) {
          <div class="kb-field">
            <span>Who can see it</span>
            <div class="kb-radio-cards">
              <label class="kb-radio-card" [class.active]="folderModal.visibility === 'company'">
                <input type="radio" name="vis" value="company" [(ngModel)]="folderModal.visibility" />
                <i class="fas fa-building"></i>
                <strong>Company</strong>
                <small>Everyone signed in</small>
              </label>
              <label class="kb-radio-card" [class.active]="folderModal.visibility === 'department'">
                <input type="radio" name="vis" value="department" [(ngModel)]="folderModal.visibility" />
                <i class="fas fa-users"></i>
                <strong>Department</strong>
                <small>One department, plus HR and Admin</small>
              </label>
              <label class="kb-radio-card" [class.active]="folderModal.visibility === 'private'">
                <input type="radio" name="vis" value="private" [(ngModel)]="folderModal.visibility" />
                <i class="fas fa-lock"></i>
                <strong>Private</strong>
                <small>Only you</small>
              </label>
            </div>
          </div>
          @if (folderModal.visibility === 'department') {
            <label class="kb-field">
              <span>Department</span>
              @if (me?.canSeeAllDepartments) {
                <select class="kb-select" [(ngModel)]="folderModal.department">
                  @for (d of me?.departments || []; track d) {
                    <option [value]="d">{{ d }}</option>
                  }
                </select>
              } @else {
                <input type="text" [value]="folderModal.department" disabled />
              }
            </label>
          }
        } @else {
          <p class="kb-muted small">Sub-folders are seen by the same people as their parent.</p>
        }
      </div>
      <footer class="kb-row-end">
        <button type="button" class="kb-btn" (click)="closeFolderModal()" [disabled]="folderModal.saving">Cancel</button>
        <button type="button" class="kb-btn kb-btn-primary" (click)="saveFolder()" [disabled]="folderModal.saving || !folderModal.name.trim()">
          {{ folderModal.saving ? 'Saving…' : folderModal.mode === 'edit' ? 'Save' : 'Create' }}
        </button>
      </footer>
    </div>
  }

  <!-- ======================================================= upload modal -->
  @if (uploadModal.open) {
    <div class="kb-overlay" (click)="closeUpload()"></div>
    <div class="kb-modal wide" role="dialog" aria-modal="true">
      <header>
        <h3>Add files to “{{ folder?.name }}”</h3>
        <button type="button" class="kb-icon-btn" (click)="closeUpload()" [disabled]="uploadModal.running"><i class="fas fa-times"></i></button>
      </header>
      <div class="kb-modal-body">
        <div
          class="kb-dropzone"
          [class.dragging]="uploadModal.dragging"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave()"
          (drop)="onDrop($event)"
          (click)="pickFiles()"
        >
          <i class="fas fa-cloud-upload-alt"></i>
          <p>Drop files here or <u>choose files</u></p>
          <small>Documents, images, videos and audio · up to 100 MB each · up to 20 at a time. For Drive and Docs material, save the link instead.</small>
        </div>
        @if (uploadModal.rows.length) {
          <ul class="kb-upload-rows">
            @for (row of uploadModal.rows; track row.file.name + row.file.size) {
              <li class="{{ row.status }}">
                <div class="kb-upload-main">
                  <span class="kb-break">{{ row.file.name }}</span>
                  <span class="kb-muted small">{{ size(row.file.size) }}</span>
                  @if (!uploadModal.running && row.status !== 'done') {
                    <button type="button" class="kb-icon-btn" (click)="removeRow(row)" title="Remove"><i class="fas fa-times"></i></button>
                  }
                </div>
                @if (row.status === 'uploading' || row.status === 'done') {
                  <div class="kb-progress"><div class="kb-progress-bar" [style.width.%]="row.percent"></div></div>
                }
                @if (row.message) {
                  <div class="kb-upload-msg">{{ row.message }}</div>
                }
              </li>
            }
          </ul>
        }
      </div>
      <footer class="kb-row-end">
        <button type="button" class="kb-btn" (click)="closeUpload()" [disabled]="uploadModal.running">
          {{ uploadDone ? 'Done' : 'Cancel' }}
        </button>
        <button type="button" class="kb-btn kb-btn-primary" (click)="startUpload()" [disabled]="uploadModal.running || !uploadReady.length">
          @if (uploadModal.running) {
            Uploading…
          } @else {
            Upload {{ uploadReady.length || '' }} file{{ uploadReady.length === 1 ? '' : 's' }}
          }
        </button>
      </footer>
    </div>
  }

  <!-- ======================================================= link modal -->
  @if (linkModal.open) {
    <div class="kb-overlay" (click)="closeLink()"></div>
    <div class="kb-modal" role="dialog" aria-modal="true">
      <header>
        <h3>Save a link in “{{ folder?.name }}”</h3>
        <button type="button" class="kb-icon-btn" (click)="closeLink()"><i class="fas fa-times"></i></button>
      </header>
      <div class="kb-modal-body">
        <label class="kb-field">
          <span>Address</span>
          <input type="url" [(ngModel)]="linkModal.url" placeholder="https://docs.google.com/…" autofocus />
          <small class="kb-muted">The page’s own title and description are read for search. You can overwrite them below.</small>
        </label>
        <label class="kb-field">
          <span>Title <small>(optional)</small></span>
          <input type="text" [(ngModel)]="linkModal.title" maxlength="160" />
        </label>
        <label class="kb-field">
          <span>Description <small>(optional)</small></span>
          <textarea rows="3" [(ngModel)]="linkModal.description" maxlength="2000"></textarea>
        </label>
        <label class="kb-field">
          <span>Tags <small>(comma-separated)</small></span>
          <input type="text" [(ngModel)]="linkModal.tags" placeholder="sop, onboarding" />
        </label>
      </div>
      <footer class="kb-row-end">
        <button type="button" class="kb-btn" (click)="closeLink()" [disabled]="linkModal.saving">Cancel</button>
        <button type="button" class="kb-btn kb-btn-primary" (click)="saveLink()" [disabled]="linkModal.saving || !linkModal.url.trim()">
          {{ linkModal.saving ? 'Saving…' : 'Save link' }}
        </button>
      </footer>
    </div>
  }

  <!-- ======================================================= move modal -->
  @if (moveModal.open && moveModal.item) {
    <div class="kb-overlay" (click)="closeMove()"></div>
    <div class="kb-modal" role="dialog" aria-modal="true">
      <header>
        <h3>Move “{{ moveModal.item.title }}”</h3>
        <button type="button" class="kb-icon-btn" (click)="closeMove()"><i class="fas fa-times"></i></button>
      </header>
      <div class="kb-modal-body">
        <p class="kb-muted small">Only folders you can add to are listed.</p>
        <ul class="kb-move-tree">
          @for (f of moveModal.folders; track f._id) {
            <li>
              <label class="kb-move-row" [class.active]="moveModal.targetId === f._id" [class.disabled]="f._id === moveModal.item.folderId">
                <input type="radio" name="moveTarget" [value]="f._id" [(ngModel)]="moveModal.targetId" [disabled]="f._id === moveModal.item.folderId" />
                <i class="fas fa-folder"></i>
                <span class="kb-move-name">{{ f.name }}</span>
                <span class="kb-vis {{ f.visibility }}">{{ visibilityLabel(f) }}</span>
                @if (f.subFolderCount) {
                  <button type="button" class="kb-icon-btn" (click)="$event.preventDefault(); toggleMoveChildren(f)" title="Sub-folders">
                    <i class="fas" [class.fa-chevron-down]="!moveModal.expanded[f._id]" [class.fa-chevron-up]="moveModal.expanded[f._id]"></i>
                  </button>
                }
              </label>
              @if (moveModal.expanded[f._id]) {
                <ul>
                  @for (c of moveModal.children[f._id] || []; track c._id) {
                    <li>
                      <label class="kb-move-row child" [class.active]="moveModal.targetId === c._id" [class.disabled]="c._id === moveModal.item.folderId">
                        <input type="radio" name="moveTarget" [value]="c._id" [(ngModel)]="moveModal.targetId" [disabled]="c._id === moveModal.item.folderId" />
                        <i class="fas fa-folder"></i>
                        <span class="kb-move-name">{{ c.name }}</span>
                      </label>
                    </li>
                  }
                </ul>
              }
            </li>
          }
        </ul>
      </div>
      <footer class="kb-row-end">
        <button type="button" class="kb-btn" (click)="closeMove()" [disabled]="moveModal.saving">Cancel</button>
        <button type="button" class="kb-btn kb-btn-primary" (click)="confirmMove()" [disabled]="moveModal.saving || !moveModal.targetId || moveModal.targetId === moveModal.item.folderId">
          {{ moveModal.saving ? 'Moving…' : 'Move here' }}
        </button>
      </footer>
    </div>
  }

  <input #fileInput type="file" multiple hidden (change)="onFilesPicked($event)" />
  <input #versionInput type="file" hidden (change)="onVersionPicked($event)" />
</div>
